import React, { useMemo } from 'react'
import { abbreviate } from '@pqt/abbreviate'
import AnimatedProgressBar, {
  AnimatedProgressBarProps,
} from '../animatedProgressBar'
import PopupTooltip from '../../overlays/popupTooltip'
import Icon from '../icon'
import { getCssColorFromColorName, ColorNames } from '../../../utils/colors'
import classnames from 'classnames/bind'
import styles from './styles.scss'

const cx = classnames.bind(styles)

const formatCount = (num: number): string => {
  return Math.abs(num) >= 1000 ? abbreviate(num, 1) : num.toString()
}

const getFractionText = (
  count: number,
  percent: number,
  totalCount?: number
): string => {
  if (count !== undefined) {
    const formattedCount = formatCount(count)
    let formattedTotal: string

    if (totalCount !== undefined) {
      // Use explicitly provided totalCount
      formattedTotal = formatCount(totalCount)
    } else if (!percent || isNaN(percent)) {
      // No totalCount and percent is 0/invalid - just show count without fraction
      return formattedCount
    } else {
      // Calculate total from percent
      formattedTotal = formatCount(Math.round(count / (percent / 100)))
    }

    return `${formattedCount}/${formattedTotal}`
  }
  return ''
}

/**
 * For both mode, we show count as follows:
 *   centered: show count only (not as fraction) and add a plus sign for positive values.
 *   non-centered: show count as fraction and percentage e.g. "5/10 (50%)"
 */
export interface LabeledProgressBarProps
  extends Omit<AnimatedProgressBarProps, 'percent'> {
  label: string
  tooltipContent?: string
  count?: number
  totalCount?: number
  percent?: number
  unitMode?: 'count' | 'countAsFraction' | 'percentage' | 'both' | 'none'
  compact?: boolean
}

const LabeledProgressBar: React.FC<LabeledProgressBarProps> = ({
  label,
  tooltipContent,
  count,
  totalCount,
  unitMode = 'percentage',
  color: colorName = 'success',
  colorNegative = 'error',
  percent: percentProp,
  centered = false,
  className,
  compact = false,
  ...progressBarProps
}) => {
  // Calculate percent from count/totalCount if not provided
  const elmId = label.toLowerCase().replace(/\s+/g, '_')

  const percent = useMemo(() => {
    if (count !== undefined && totalCount !== undefined && totalCount > 0) {
      return Math.round((count / totalCount) * 100) // Calculate percent (takes precedence)
    }
    if (percentProp !== undefined) {
      return percentProp // Use provided percent as fallback
    }
    return 0 // Default to 0 if can't calculate
  }, [percentProp, count, totalCount])

  const unitsDisplay = useMemo(() => {
    if (unitMode === 'none') {
      return null
    }

    const isNegative = centered && percent < 0
    // eslint-disable-next-line no-nested-ternary
    const activeColorName = isNegative
      ? colorNegative
      : percent === 0
      ? 'gray'
      : colorName

    const textColor = getCssColorFromColorName(activeColorName as ColorNames)

    let displayText = ''

    switch (unitMode) {
      case 'count':
        if (count !== undefined) {
          const formattedCount = formatCount(count)
          if (centered && count > 0) {
            displayText = `+${formattedCount}`
          } else if (centered && count < 0) {
            displayText = formattedCount
          } else {
            displayText = formattedCount
          }
        }
        break

      case 'countAsFraction':
        displayText = getFractionText(count ?? 0, percent, totalCount)
        break

      case 'percentage':
        displayText = `${percent}%`
        break

      case 'both':
        if (count !== undefined) {
          const fractionText = getFractionText(count, percent, totalCount)
          const formattedCount = formatCount(count)
          let countText = formattedCount
          if (centered) {
            // for centered, show count only (not as fraction) and add a plus sign for positive values
            if (count > 0) {
              countText = `+${formattedCount}`
            }
            displayText = `${countText} (${percent}%)`
          } else {
            // for non-centered, show count as fraction and percentage
            displayText = `${fractionText} (${percent}%)`
          }
        } else {
          displayText = `${percent}%`
        }
        break
    }

    if (!displayText) return null

    return (
      <span
        className={cx('units')}
        style={{ color: textColor }}
        data-elm-id={`metric_${elmId}_value`}
      >
        {displayText}
      </span>
    )
  }, [
    unitMode,
    count,
    percent,
    totalCount,
    centered,
    colorName,
    colorNegative,
    elmId,
  ])

  return (
    <div
      className={cx('labeled-progress-bar', className)}
      data-elm-id={`metric_${elmId}`}
    >
      <div className={cx('header', { 'header--compact': compact })}>
        <span className={cx('label')} data-elm-id={`metric_${elmId}_label`}>
          {label}{' '}
          {tooltipContent && (
            // @ts-ignore next-line
            <PopupTooltip
              content={tooltipContent}
              position='top left'
              wide={false}
              trigger={
                <Icon
                  color='grayMedium'
                  name='info circle'
                  fitted
                  style={{ verticalAlign: 'super' }}
                  size='small'
                />
              }
            />
          )}
        </span>
        {unitsDisplay}
      </div>
      <AnimatedProgressBar
        color={colorName}
        colorNegative={colorNegative}
        percent={percent}
        centered={centered}
        {...progressBarProps}
      />
    </div>
  )
}

export default LabeledProgressBar
